import Link from '@mui/material/Link';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid/models';
import React from 'react';
import { Link as LinkRouter } from 'react-router-dom';
import { useAuthContext } from '../../context/auth';
import { GraceLapseReinstatementResponse, Policy2 } from '../../types/policy';
import { formatName, getPolicyStatus } from '../../util/util';
import GridWithToolbar from '../grid/GridWithToolbar';
import {
	POLICYHOLDER__APPLICATIONS__TABLE_HEADER,
	POLICYHOLDER__APPLICATIONS__TABLE_HEADER__DATE_CREATED,
	POLICYHOLDER__APPLICATIONS__TABLE_HEADER__DATE_CREATED_CLASSNAME,
	POLICYHOLDER__APPLICATIONS__TABLE_HEADER__DATE_CREATED_FIELD_NAME,
	POLICYHOLDER__APPLICATIONS__TABLE_HEADER__POLICY_ID,
	POLICYHOLDER__APPLICATIONS__TABLE_HEADER__POLICY_ID_CLASSNAME,
	POLICYHOLDER__APPLICATIONS__TABLE_HEADER__POLICY_ID_FIELD_NAME,
	POLICYHOLDER__APPLICATIONS__TABLE_HEADER__PRODUCT,
	POLICYHOLDER__APPLICATIONS__TABLE_HEADER__PRODUCT_CLASSNAME,
	POLICYHOLDER__APPLICATIONS__TABLE_HEADER__PRODUCT_FIELD_NAME,
	POLICYHOLDER__APPLICATIONS__TABLE_HEADER__STATUS,
	POLICYHOLDER__APPLICATIONS__TABLE_HEADER__STATUS_CLASSNAME,
	POLICYHOLDER__APPLICATIONS__TABLE_HEADER__STATUS_FIELD_NAME,
	POLICYHOLDER__APPLICATIONS__TABLE_ROW_ID,
} from './data/constants';

interface ApplicationsTableProps {
	policies: Policy2[];
	graceLapses: GraceLapseReinstatementResponse[];
	loading: boolean;
	isRefetching: boolean;
}

interface ApplicationsTableRow {
	displayId: string;
	product: string;
	dateCreated: number;
	dateCreatedFormatted: string;
	status: string;
}

const NewApplicationsTable = (props: ApplicationsTableProps) => {
	const authData = useAuthContext();
	const { policies, graceLapses, loading, isRefetching } = props;

	const LinkCell = (params: GridRenderCellParams<ApplicationsTableRow>) => (
		<Link
			underline='hover'
			component={LinkRouter}
			to={`/policy/${params.row.displayId}/details`}
		>
			{params.value}
		</Link>
	);

	const columns: GridColDef<ApplicationsTableRow>[] = [
		{
			field: POLICYHOLDER__APPLICATIONS__TABLE_HEADER__POLICY_ID_FIELD_NAME,
			headerName: POLICYHOLDER__APPLICATIONS__TABLE_HEADER__POLICY_ID,
			headerClassName:
				POLICYHOLDER__APPLICATIONS__TABLE_HEADER__POLICY_ID_CLASSNAME,
			flex: 0.2,
			renderCell: LinkCell,
		},
		{
			field: POLICYHOLDER__APPLICATIONS__TABLE_HEADER__PRODUCT_FIELD_NAME,
			headerName: POLICYHOLDER__APPLICATIONS__TABLE_HEADER__PRODUCT,
			headerClassName:
				POLICYHOLDER__APPLICATIONS__TABLE_HEADER__PRODUCT_CLASSNAME,
			flex: 0.2,
		},
		{
			field: POLICYHOLDER__APPLICATIONS__TABLE_HEADER__DATE_CREATED_FIELD_NAME,
			headerName: POLICYHOLDER__APPLICATIONS__TABLE_HEADER__DATE_CREATED,
			headerClassName:
				POLICYHOLDER__APPLICATIONS__TABLE_HEADER__DATE_CREATED_CLASSNAME,
			flex: 0.2,
			renderCell: (params: GridRenderCellParams<ApplicationsTableRow>) =>
				params.row.dateCreatedFormatted,
		},
		{
			field: POLICYHOLDER__APPLICATIONS__TABLE_HEADER__STATUS_FIELD_NAME,
			headerName: POLICYHOLDER__APPLICATIONS__TABLE_HEADER__STATUS,
			headerClassName:
				POLICYHOLDER__APPLICATIONS__TABLE_HEADER__STATUS_CLASSNAME,
			flex: 0.2,
		},
	];

	const formattedData: ApplicationsTableRow[] = policies.map((policy) => {
		const policyGraceLapse = graceLapses.find((g) =>
			g ? g.gracePeriod.policyLocator === policy.locator : null,
		);

		const status = getPolicyStatus(policy, policyGraceLapse);

		const data: ApplicationsTableRow = {
			displayId: policy.displayId,
			product: formatName(policy.productName),
			dateCreated: parseInt(policy.createdTimestamp),
			dateCreatedFormatted: authData.getZonedFormattedDateString(
				policy.createdTimestamp,
			),
			status,
		};

		return data;
	});

	return (
		<GridWithToolbar
			rowId={POLICYHOLDER__APPLICATIONS__TABLE_ROW_ID}
			title={POLICYHOLDER__APPLICATIONS__TABLE_HEADER}
			isRefetching={isRefetching}
			data={formattedData || []}
			columns={columns}
			loading={loading}
			autoHeight
			pageSize={5}
		/>
	);
};

export default NewApplicationsTable;
